import { Component, Input, Output, EventEmitter, inject, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { Entity } from '../../../../core/data-models/Entity';
import { JobGrade } from '../../../../core/data-models/job-grade';
import { BandLevel } from '../../../../core/data-models/band-level';
import { LanguageService } from '../../../../core/services/language.service';
import { ModuleName } from '../../../../shared/enums/ModuleName';
import { ThemeModule } from '../../../../shared/Theme/theme.module';
import { ChildrenAnimation, inOutAnimation, slidesAniamtion } from '../../../../shared/animations/allAnimations';

export interface JobGradeTableOptions {
    data: JobGrade[];
    columns: { field: string; title: string }[];
}

/**
 * Job Grade Settings Component
 * 
 * Handles CRUD operations for job grades including:
 * - Display job grades in a table
 * - Add new job grade
 * - Edit existing job grade
 * - Delete job grade
 */
@Component({
    selector: 'job-grade-settings',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ThemeModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
    templateUrl: './job-grade-settings.component.html',
    styleUrls: ['./job-grade-settings.component.scss'],
    animations: [inOutAnimation, slidesAniamtion, ChildrenAnimation]
})
export class JobGradeSettingsComponent {
    private languageService = inject(LanguageService);

    readonly ModuleNames = ModuleName;

    @Input() selectedEntity!: Entity;
    @Input() language: string = 'EN';
    @Input() jobOptions!: JobGradeTableOptions;
    @Input() bandLevels: BandLevel[] = [];

    @Output() addJobGrade = new EventEmitter<void>();
    @Output() editJobGrade = new EventEmitter<{ index: number; item: JobGrade }>();
    @Output() deleteJobGrade = new EventEmitter<{ index: number; item: JobGrade }>();

    /**
     * Extract name in current language from translation array
     */
    extractNameLanguage(name: any): string {
        return this.languageService.extractNameLanguage(name);
    }

    /**
     * Extract band level name by key
     */
    extractBandLevelName(bandLevelKey: string): string {
        const bandLevel = this.bandLevels.find(b => b.key === bandLevelKey);
        return bandLevel ? this.extractNameLanguage(bandLevel.name) : '';
    }

    onAddJobGrade(): void {
        this.addJobGrade.emit();
    }

    onEditJobGrade(index: number, item: JobGrade): void {
        this.editJobGrade.emit({ index, item });
    }

    onDeleteJobGrade(index: number, item: JobGrade): void {
        this.deleteJobGrade.emit({ index, item });
    }
}
